import { reactive } from 'vue';
import { timeApi } from './api';
import { isModuleActive, type TenantModuleAccess } from './moduleSwitcher';

// Administration › Statutory rules (ADR 0091): who may open the page, decided in
// ONE place for the router guard, the sidebar entry and the page itself.
//
// The rules and a workspace's agreements live in time-api, which admits two
// kinds of people to every `/statutory-rules` route: the workspace owner or a
// suite admin (the master registry, as user-api reports it — the same set
// `hasAdminRole` reads here; a consented support grant is not one), and a Time
// member holding `compliance.manage`. The first this app knows from the tenant
// list. The second it cannot know — Time's roles are Time's — so it asks
// time-api (`GET /statutory-rules/access`) and keeps the answer per user and
// tenant for the session: the sidebar must not ask again on every render. The
// notices about a changed statute deep-link here, which is why the route is
// guarded at all and why the path is fixed.

/** The page. notification-api's `workspace.statutoryRules` link points here. */
export const STATUTORY_RULES_PATH = '/admin/statutory-rules';

/** time-api's answer for an admitted caller. */
export interface StatutoryRulesAccess {
  canManage: true;
  via: 'workspace_admin' | 'compliance_manage';
  /** Time's `settings.manage` (or time_admin): may add a country's work-time models. */
  canAddWorkTimeModels: boolean;
}

export type StatutoryRulesAccessAnswer =
  | { state: 'granted'; access: StatutoryRulesAccess }
  /** 403: neither the workspace admin nor a `compliance.manage` holder. */
  | { state: 'refused' }
  /** 404: the workspace has no active Time module, so there is nothing to manage. */
  | { state: 'timeInactive' }
  /** Anything else (unreachable, provisioning, a malformed answer): not kept, asked again next time. */
  | { state: 'unavailable'; error: unknown };

/** Whether the workspace runs Time — without it nobody has rules to manage here. */
export function tenantRunsTime(tenant: TenantModuleAccess | null | undefined): boolean {
  return !!tenant && isModuleActive(tenant, 'time_tracking');
}

// Settled answers, per `user|tenant`. Reactive, so the sidebar shows the entry
// the moment a probe it started comes back. An answer is the user's in that
// tenant: another account in this tab, or another tenant, is another key.
const answers = reactive(new Map<string, StatutoryRulesAccessAnswer>());
const inflight = new Map<string, Promise<StatutoryRulesAccessAnswer>>();
// Bumped by `forgetStatutoryRulesAccess`: a probe that started before it must
// not write its answer back into the emptied cache.
let generation = 0;

const keyOf = (userId: string, tenantSlug: string) => `${userId}|${tenantSlug}`;

function readAnswer(data: unknown): StatutoryRulesAccessAnswer {
  const body = data as Partial<StatutoryRulesAccess> | null;
  // A 200 that is not the answer (a proxy's stub) admits nobody.
  if (!body || body.canManage !== true) return { state: 'unavailable', error: new Error('GET /statutory-rules/access did not answer an access') };
  return {
    state: 'granted',
    access: {
      canManage: true,
      via: body.via === 'compliance_manage' ? 'compliance_manage' : 'workspace_admin',
      canAddWorkTimeModels: body.canAddWorkTimeModels === true,
    },
  };
}

/** The answer this session already holds for the user in that tenant, if any. */
export function cachedStatutoryRulesAccess(userId: string | null | undefined, tenantSlug: string | null | undefined) {
  if (!userId || !tenantSlug) return undefined;
  return answers.get(keyOf(userId, tenantSlug));
}

/**
 * Ask time-api whether the user may manage the statutory rules of the tenant.
 * A settled answer (granted, refused, Time inactive) is kept for the session;
 * concurrent asks share one request. Never throws. In a demo it is asked as
 * the persona worn, and a persona without a seat there is a refusal like any
 * other — never a reason to take the persona off (issue 807).
 */
export function probeStatutoryRulesAccess(
  userId: string | null | undefined,
  tenantSlug: string | null | undefined,
): Promise<StatutoryRulesAccessAnswer> {
  if (!userId || !tenantSlug) return Promise.resolve({ state: 'refused' });
  const key = keyOf(userId, tenantSlug);
  const known = answers.get(key);
  if (known) return Promise.resolve(known);
  const pending = inflight.get(key);
  if (pending) return pending;
  const startedIn = generation;
  const request = timeApi
    .get('/statutory-rules/access', { headers: { 'X-Tenant-Slug': tenantSlug }, suppressGlobalError: true, keepDemoPersona: true })
    .then(
      ({ data }) => readAnswer(data),
      (error: unknown): StatutoryRulesAccessAnswer => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 403) return { state: 'refused' };
        if (status === 404) return { state: 'timeInactive' };
        return { state: 'unavailable', error };
      },
    )
    .then((answer) => {
      if (startedIn !== generation) return answer;
      inflight.delete(key);
      if (answer.state !== 'unavailable') answers.set(key, answer);
      return answer;
    });
  inflight.set(key, request);
  return request;
}

/** The workspace admin facts the decision reads (the tenants store's). */
export interface StatutoryRulesViewer {
  userId: string | null | undefined;
  tenantSlug: string | null | undefined;
  /** Owner or suite admin (`tenantsStore.hasAdminRole`). */
  isWorkspaceAdmin: boolean;
  /** Whether the tenant runs Time (`tenantRunsTime`). */
  runsTime: boolean;
}

/**
 * The route guard's decision. A workspace admin passes without a request (the
 * page then says so if Time is not active); anybody else passes only on
 * time-api's 200 — fail-closed: no Time, a refusal or no answer at all keeps
 * them out, and time-api decides again on every request behind the page.
 */
export async function mayOpenStatutoryRules(viewer: StatutoryRulesViewer): Promise<boolean> {
  if (viewer.isWorkspaceAdmin) return true;
  if (!viewer.runsTime) return false;
  const answer = await probeStatutoryRulesAccess(viewer.userId, viewer.tenantSlug);
  return answer.state === 'granted';
}

/**
 * The sidebar's decision, synchronous: only for a tenant that runs Time, then
 * for the workspace admin, or for whoever this session's probe admitted. Use
 * `probeStatutoryRulesAccess` to learn the latter; never probe without Time.
 */
export function showsStatutoryRulesNav(viewer: StatutoryRulesViewer): boolean {
  if (!viewer.runsTime) return false;
  if (viewer.isWorkspaceAdmin) return true;
  return cachedStatutoryRulesAccess(viewer.userId, viewer.tenantSlug)?.state === 'granted';
}

/**
 * Drop every kept answer: on sign-out, and whenever the tenants store asks the
 * workspace to re-read itself (a tenant switch, the membership list re-read, the
 * end of setup) — a role given or taken in the meantime must count.
 */
export function forgetStatutoryRulesAccess(): void {
  generation += 1;
  answers.clear();
  inflight.clear();
}
